import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { SharedSettings } from "../models/shared-settings";
import {DateTime} from "luxon";
import {Observable} from "rxjs";
import {PieceRateSiteWeek} from "../models/piece-rate-site-week";
import {
    PieceRateAdjustment,
    PieceRateAdjustmentEntryBuilder
} from "../models/piece-rate-adjustment";
import {PieceRateAdjSummary} from "../models/piece-rate-summary";
import {url} from "../helpers/url";
import {CombinedShiftTotals} from "../models/combined-shift-totals";
import {PieceRateActivity} from "../models/piece-rate-activity";
import {WeeklyTotals} from "../models/weekly-totals";

@Injectable({
  providedIn: 'root'
})
export class PieceRateRequestService {

    constructor(private http: HttpClient,
              private settings: SharedSettings) { }

    getPieceRateAdjustment(siteId: string, date: DateTime, shiftId: number): Observable<PieceRateAdjustment> {
        return this.http.get<PieceRateAdjustment>(this.settings.apiBaseUrl + `PieceRate/Adjustments?siteId=${siteId}&date=${date.toISODate()}&shiftId=${shiftId}`);
    }

    getPieceRateSummary(siteId: string, date: DateTime, shiftId: number, activityId: number): Observable<PieceRateAdjSummary> {
        return this.http.get<PieceRateAdjSummary>(this.settings.apiBaseUrl
            + url`PieceRate/Summaries?siteId=${siteId}&date=${date.toISODate()}&shiftId=${shiftId}&adjustmentType=${activityId}`)
    }
    getPieceRateSiteWeek(siteId: string, weekEndDate: DateTime): Observable<PieceRateSiteWeek> {
        return this.http.get<PieceRateSiteWeek>(this.settings.apiBaseUrl + `PieceRate/Adjustments/weeks/${siteId}/${weekEndDate.toISODate()}`);
    }

    getPieceRateWeekStatus(siteId: string, weekEnding: DateTime): Observable<string[]> {
        return this.http.get<string[]>(this.settings.apiBaseUrl + url`PieceRate/Adjustments/weeks/${siteId}/${weekEnding.toISODate()}/status`);
    }

    getCombinedShiftTotals(siteId: string, date: DateTime,
                           shiftId: number, activityId: number): Observable<CombinedShiftTotals> {
        return this.http.get<CombinedShiftTotals>(this.settings.apiBaseUrl
            + 'PieceRate/Summaries/combinedShifts'
            + url`?siteId=${siteId}&date=${date.toISODate()}&shiftId=${shiftId}&adjustmentType=${activityId}`)
    }

    createPieceRateAdjustment(siteId?: string, date?: DateTime, shiftId?: number): Observable<PieceRateAdjustment> {
        const post = {
            siteId: siteId,
            date: date?.toISODate(),
            shiftId: shiftId,
            activityConfigs: []
        };

        return this.http.post<PieceRateAdjustment>(this.settings.apiBaseUrl + url`PieceRate/Adjustments`, post)
    }

    createPieceRateSummary(siteId?: string, date?: DateTime, shiftId?: number, activityId?: number): Observable<PieceRateAdjSummary> {
        const post = {
            siteId: siteId,
            date: date?.toISODate(),
            shiftId: shiftId,
        };

        return this.http.post<PieceRateAdjSummary>(this.settings.apiBaseUrl + url`PieceRate/Summaries`, post)
    }

    putPieceRateSummary(summary: PieceRateAdjSummary): Observable<PieceRateAdjSummary> {
        return this.http.put<PieceRateAdjSummary>(this.settings.apiBaseUrl + url`PieceRate/Summaries`, {
            ...summary,
            date: summary.date?.toISODate()
        });
    }

    postBuilderEntry(adjustmentId: number, entry: PieceRateAdjustmentEntryBuilder):
        Observable<PieceRateAdjustmentEntryBuilder> {
        return this.http.post<PieceRateAdjustmentEntryBuilder>(this.settings.apiBaseUrl +
            url`PieceRate/Adjustments/${adjustmentId}/entries/builders`, entry)
    }

    postBatchBuilderEntries(adjustmentId: number, entry: PieceRateAdjustmentEntryBuilder[]):
        Observable<PieceRateAdjustmentEntryBuilder[]> {
        return this.http.post<PieceRateAdjustmentEntryBuilder[]>(this.settings.apiBaseUrl +
            url`PieceRate/Adjustments/${adjustmentId}/batch/builders`, entry)
    }

    updateBuilderEntry(adjustmentId: number, builderEntryId: number, entry: PieceRateAdjustmentEntryBuilder):
        Observable<PieceRateAdjustmentEntryBuilder> {
        return this.http.put<PieceRateAdjustmentEntryBuilder>(this.settings.apiBaseUrl +
            url`PieceRate/Adjustments/${adjustmentId}/entries/builders/${builderEntryId}`, entry)
    }

    deleteBuilderEntry(adjustmentId: number, builderEntryId: number): Observable<any> {
        return this.http.delete(this.settings.apiBaseUrl
            + url`PieceRate/Adjustments/${adjustmentId}/entries/builders/${builderEntryId}`)
    }

    postSiteWeekClosed(siteId: string, weekEnding: DateTime, closed: boolean): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type':  'application/json'
            })
        };

        return this.http.post(this.settings.apiBaseUrl +
            url`PieceRate/Adjustments/weeks/${siteId}/${weekEnding.toISODate()}/closed`, closed, httpOptions)
    }

    postSiteWeekCombinedShifts(siteId: string, weekEnding: DateTime, combinedShifts: boolean): Observable<any> {
        return this.http.post(this.settings.apiBaseUrl +
            url`PieceRate/Adjustments/weeks/${siteId}/${weekEnding.toISODate()}/combinedShifts`, combinedShifts)
    }

    pieceRateReset(adjustmentId: number, pieceRateActivityId: number): Observable<any> {
        return this.http.delete(this.settings.apiBaseUrl
            + url`PieceRate/Adjustments/${adjustmentId}/entries/bytype/${pieceRateActivityId}`)
    }

    checkWeeklyTotals(weekEnding: DateTime, pieceRateActivity: PieceRateActivity, siteId: string): Observable<WeeklyTotals[]> {
        return this.http.get<WeeklyTotals[]>(this.settings.apiBaseUrl
            + url`PieceRate/WeekTotals/${weekEnding.toISODate()}/${pieceRateActivity.id}/${siteId}`);
    }
}
